import React, { useMemo, useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import axios from "../../../axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Card, CardContent } from "../../../components/ui/card";
import { toast } from "react-toastify";
import { getAllowedBranchIds } from "../../../utils/tokenUtils";

import "dayjs/locale/ar";
import "dayjs/locale/en";
import dayjs from "dayjs";
export default function SalesByDayReport() {
  const { t, i18n } = useTranslation(); // 👈 هنا نستدعي i18n

  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const defaultBranchId = parseInt(localStorage.getItem("branchId"), 10);

  const [filters, setFilters] = useState({
    dateFrom: dayjs().startOf("month").format("YYYY-MM-DD"),
    dateTo: dayjs().endOf("month").format("YYYY-MM-DD"),
    userId: "",
    paymentMethodId: "",
    branchId: "",
  });

  const [branches, setBranches] = useState([]);
 
  dayjs.locale(i18n.language === "ar" ? "ar" : "en");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    
    // جلب الفروع وتصفيتها حسب الفروع المسموحة
    axios.get(`/Branch/by-company/${companyId}`).then(r => {
      const allowedBranchIds = getAllowedBranchIds();
      // ✅ القاعدة: إذا كانت القائمة فارغة → المستخدم له صلاحية على كل الفروع
      if (allowedBranchIds && allowedBranchIds.length > 0) {
        const filteredBranches = r.data.filter(b => allowedBranchIds.includes(b.id));
        setBranches(filteredBranches);
        if (defaultBranchId > 0 && filteredBranches.some(b => b.id === defaultBranchId)) {
          setFilters(prev => ({ ...prev, branchId: defaultBranchId.toString() }));
        } else if (filteredBranches.length > 0) {
          setFilters(prev => ({ ...prev, branchId: filteredBranches[0].id.toString() }));
        }
      } else {
        // المستخدم له صلاحية على كل الفروع → عرض الكل
        setBranches(r.data);
      }
    });
  }, [companyId, defaultBranchId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/Invoice/SalesByDay", {
        params: {
          companyId,
          branchId: filters.branchId ? Number(filters.branchId) : 0,
          dateFrom: `${filters.dateFrom}T00:00:00`,
          dateTo: `${filters.dateTo}T23:59:59`,
          userId: filters.userId ? Number(filters.userId) : null,
          paymentMethodId: filters.paymentMethodId ? Number(filters.paymentMethodId) : null,
          lang: i18n.language === "ar" ? "ar" : "en",
        },
      });
      const normalized = Array.isArray(res.data)
        ? res.data.map((r, i) => ({
            id: i + 1,
            date: r.date ?? r.Date ?? null,
            dayName: r.dayName ?? r.DayName ?? "",
            netSales: r.netSales ?? r.NetSales ?? 0,
          }))
        : [];
      setRows(normalized);
    } catch (err) {
      toast.error("❌ فشل جلب التقرير");
    } finally {
      setLoading(false);
    }
  };

const columns = [
  {
    field: "date",
    headerName: t("ledgerReport.date"),
    flex: 1,
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) =>
      params?.value ? dayjs(params.value).format("YYYY-MM-DD") : "",
  },
  {
    field: "dayName",
    headerName: t("ledgerReport.dayName") || "اليوم",
    flex: 1,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "netSales",
    headerName: t("invoice.summary.net"),
    flex: 1,
    type: "number",
    headerAlign: "center",
    align: "center",
  },
];

  const grandTotal = useMemo(() => rows.reduce((sum, r) => sum + (r.netSales || 0), 0), [rows]);
  const averageSales = useMemo(() => (rows.length ? grandTotal / rows.length : 0), [rows, grandTotal]);
  const totalDays = rows.length;

  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">📊 {t("salesMenu.dailySalesReport")}</h2>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchReport}>{t("invoicesReport.fetchInvoices")}</Button>
          </div>
        </div>

        {/* إحصائيات سريعة */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="p-3 rounded bg-indigo-50 border text-center">
            <div className="text-sm text-indigo-700 font-semibold mb-1">عدد الأيام</div>
            <div className="text-xl font-bold text-indigo-900">{totalDays}</div>
          </div>
          <div className="p-3 rounded bg-green-50 border text-center">
            <div className="text-sm text-green-700 font-semibold mb-1">إجمالي المبيعات</div>
            <div className="text-xl font-bold text-green-900">{grandTotal.toFixed(2)}</div>
          </div>
          <div className="p-3 rounded bg-amber-50 border text-center">
            <div className="text-sm text-amber-700 font-semibold mb-1">متوسط اليومية</div>
            <div className="text-xl font-bold text-amber-900">{averageSales.toFixed(2)}</div>
          </div>
        </div>

        {/* الفلاتر */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
          <div>
            <label>{t("ledgerReport.fromDate")}</label>
            <Input type="date" name="dateFrom" value={filters.dateFrom} onChange={handleChange} />
          </div>
          <div>
            <label>{t("ledgerReport.toDate")}</label>
            <Input type="date" name="dateTo" value={filters.dateTo} onChange={handleChange} />
          </div>
          <div>
            <label>{t("ledgerReport.branch", "الفرع")}</label>
            <select
              name="branchId"
              value={filters.branchId}
              onChange={handleChange}
              className="w-full border p-2 rounded"
            >
              <option value="">{t("common.all")}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.nameAr || b.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={fetchReport}>{t("invoicesReport.fetchInvoices")}</Button>
          </div>
        </div>

        {/* الجدول */}
        <div style={{ height: "60vh", width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            loading={loading}
            localeText={{
              noRowsLabel: 'لا توجد بيانات',
              toolbarColumns: 'الأعمدة',
              toolbarFilters: 'الفلاتر',
              toolbarDensity: 'الكثافة',
              toolbarExport: 'تصدير',
            }}
          />
        </div>

        {/* الإجمالي */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="p-3 bg-blue-50 border rounded text-right font-bold">
            {t("invoice.summary.net")}: {" "}
            <span className="text-green-700">{grandTotal.toFixed(2)}</span>
          </div>
          <div className="p-3 bg-indigo-50 border rounded text-right font-bold">
            متوسط اليومية: <span className="text-indigo-700">{averageSales.toFixed(2)}</span>
          </div>
          <div className="p-3 bg-amber-50 border rounded text-right font-bold">
            عدد الأيام: <span className="text-amber-700">{totalDays}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
